/**
 * Standardized Editing Interface Utilities
 * Provides consistent editing patterns across all site components
 */

// Standard form field configurations
export const FORM_FIELD_TYPES = {
  TEXT: 'text',
  EMAIL: 'email',
  DATE: 'date',
  LOCATION: 'location',
  SELECT: 'select',
  TEXTAREA: 'textarea',
  NUMBER: 'number'
};

// Standard validation patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  DATE_FLEXIBLE: /^\d{4}$|^\d{1,2}\s+\w{3}\s+\d{4}$|^\w{3}\s+\d{4}$|^\d{1,2}\/\d{1,2}\/\d{4}$/,
  REQUIRED: /\S+/ // At least one non-whitespace character
};

// Standard error messages
export const ERROR_MESSAGES = {
  REQUIRED: 'This field is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_DATE: 'Please enter a valid date (e.g., 1990, 15 Mar 1990)',
  GENERIC_ERROR: 'Please check this field'
};

/**
 * Standardized form field validator
 */
export class FormValidator {
  static validateField(value, fieldType, isRequired = false) {
    const errors = [];
    
    // Check required fields
    if (isRequired && !VALIDATION_PATTERNS.REQUIRED.test(value || '')) {
      errors.push(ERROR_MESSAGES.REQUIRED);
    }
    
    // Skip type validation if field is empty and not required
    if (!value || !value.trim()) {
      return errors;
    }
    
    // Type-specific validation
    switch (fieldType) {
      case FORM_FIELD_TYPES.EMAIL:
        if (!VALIDATION_PATTERNS.EMAIL.test(value)) {
          errors.push(ERROR_MESSAGES.INVALID_EMAIL);
        }
        break;
      case FORM_FIELD_TYPES.DATE:
        if (!VALIDATION_PATTERNS.DATE_FLEXIBLE.test(value)) {
          errors.push(ERROR_MESSAGES.INVALID_DATE);
        }
        break;
      default:
        // No additional validation for other types
        break;
    }
    
    return errors;
  }
  
  static validateForm(formData, fieldConfigs) {
    const errors = {};
    
    Object.entries(fieldConfigs).forEach(([fieldName, config]) => {
      const value = formData[fieldName];
      const fieldErrors = this.validateField(value, config.type, config.required);
      
      if (fieldErrors.length > 0) {
        errors[fieldName] = fieldErrors[0]; // Show first error only
      }
    });
    
    return errors;
  }
}

/**
 * Standardized editing state manager
 */
export class EditingStateManager {
  constructor(initialData = {}) {
    this.history = [{ ...initialData }];
    this.currentIndex = 0;
    this.listeners = [];
  }
  
  getCurrentData() {
    return { ...this.history[this.currentIndex] };
  }
  
  updateData(newData) {
    // Remove any future history when making new changes
    this.history = this.history.slice(0, this.currentIndex + 1);
    
    // Add new state
    this.history.push({ ...newData });
    this.currentIndex = this.history.length - 1;
    
    this.notifyListeners();
  }
  
  canUndo() {
    return this.currentIndex > 0;
  }
  
  canRedo() {
    return this.currentIndex < this.history.length - 1;
  }
  
  undo() {
    if (this.canUndo()) {
      this.currentIndex--;
      this.notifyListeners();
      return this.getCurrentData();
    }
    return null;
  }
  
  redo() {
    if (this.canRedo()) {
      this.currentIndex++;
      this.notifyListeners();
      return this.getCurrentData();
    }
    return null;
  }
  
  getHistoryLength() {
    return this.history.length;
  }
  
  addListener(callback) {
    this.listeners.push(callback);
  }
  
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }
  
  notifyListeners() {
    this.listeners.forEach(callback => callback(this.getCurrentData()));
  }
}

/**
 * Date input helper for flexible formats
 */
export class DateInputHelper {
  static formatDateForDisplay(dateString) {
    if (!dateString) return '';
    
    // Handle various input formats and normalize for display
    const trimmed = dateString.trim();
    
    // Already in a good format
    if (VALIDATION_PATTERNS.DATE_FLEXIBLE.test(trimmed)) {
      return trimmed;
    }
    
    return trimmed; // Return as-is if not matching patterns
  }
  
  static suggestDateFormats(partialInput) {
    if (!partialInput || partialInput.length < 2) return [];
    
    const suggestions = [];
    const currentYear = new Date().getFullYear();
    
    // If it looks like a year
    if (/^\d{4}$/.test(partialInput)) {
      suggestions.push(partialInput);
    }
    
    // If it looks like the start of a year
    if (/^\d{1,3}$/.test(partialInput) && partialInput.length >= 3) {
      const year = parseInt(partialInput);
      if (year >= 180 && year <= currentYear) {
        suggestions.push(`${year}0`);
      }
    }
    
    return suggestions;
  }
}

/**
 * Location input helper for flexible formats
 */
export class LocationInputHelper {
  static formatLocationForDisplay(locationString) {
    if (!locationString) return '';
    
    // Basic formatting - capitalize first letter of each word
    return locationString
      .split(',')
      .map(part => part.trim())
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(', ');
  }
  
  static suggestLocations(partialInput) {
    if (!partialInput || partialInput.length < 2) return [];
    
    // Common location suggestions
    const commonLocations = [
      'Italy',
      'Rome, Italy',
      'Naples, Italy',
      'New York, NY',
      'Boston, MA',
      'Toronto, Canada',
      'London, England'
    ];
    
    return commonLocations.filter(location => 
      location.toLowerCase().includes(partialInput.toLowerCase())
    );
  }
}

/**
 * Visual feedback manager for editing interfaces
 */
export class VisualFeedbackManager {
  static showSuccess(message = 'Changes saved successfully!', duration = 3000) {
    return {
      type: 'success',
      message,
      duration,
      timestamp: Date.now()
    };
  }
  
  static showError(message = 'An error occurred. Please try again.', duration = 5000) {
    return {
      type: 'error',
      message,
      duration,
      timestamp: Date.now()
    };
  }
  
  static showWarning(message, duration = 4000) {
    return {
      type: 'warning',
      message,
      duration,
      timestamp: Date.now()
    };
  }
  
  static showInfo(message, duration = 3000) {
    return {
      type: 'info',
      message,
      duration,
      timestamp: Date.now()
    };
  }
}

/**
 * Standard form field component configuration
 */
export const createStandardFieldConfig = (type, options = {}) => {
  const baseConfig = {
    type,
    required: false,
    placeholder: '',
    helpText: '',
    validation: [],
    ...options
  };
  
  // Add type-specific defaults
  switch (type) {
    case FORM_FIELD_TYPES.EMAIL:
      baseConfig.placeholder = baseConfig.placeholder || 'Enter email address';
      break;
    case FORM_FIELD_TYPES.DATE:
      baseConfig.placeholder = baseConfig.placeholder || 'e.g., 1990, 15 Mar 1990';
      baseConfig.helpText = baseConfig.helpText || 'Accepts various date formats';
      break;
    case FORM_FIELD_TYPES.LOCATION:
      baseConfig.placeholder = baseConfig.placeholder || 'e.g., New York, NY';
      baseConfig.helpText = baseConfig.helpText || 'City, State or Country';
      break;
    default:
      break;
  }
  
  return baseConfig;
};

export default {
  FormValidator,
  EditingStateManager,
  DateInputHelper,
  LocationInputHelper,
  VisualFeedbackManager,
  createStandardFieldConfig,
  FORM_FIELD_TYPES,
  VALIDATION_PATTERNS,
  ERROR_MESSAGES
};